import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiUser, FiLock, FiSave, FiGlobe, FiShield, FiDroplet,
  FiCheck, FiBriefcase, FiAtSign, FiEye, FiEyeOff, FiChevronRight,
  FiArrowRight, FiSettings, FiSliders, FiSidebar, FiUserCheck, FiPlus,
} from 'react-icons/fi';
import { authAPI, settingsAPI } from '../../services/api';
import { fetchTenantConfig } from '../../store/slices/settingsSlice';
import { setUserLanguage, setTenantLanguage } from '../../store/slices/languageSlice';
import { SUPPORTED_LANGUAGES, getLanguageMeta } from '../../i18n/supportedLanguages';
import { getLocalized } from '../../i18n/getLocalized';
import { hasPermission } from '../../utils/permissions';
import { getTimezoneOptions, getCurrencyOptions, getCountryOptions } from '../../utils/localeOptions';
import SearchableSelect from '../../components/SearchableSelect';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader/PageHeader';
import DelegationTab from './DelegationTab';
import './Settings.scss';

function SectionHeader({ icon: Icon, title, description }) {
  return (
    <div className="settings-card__header">
      <div className="settings-card__icon"><Icon /></div>
      <div className="settings-card__heading">
        <h3>{title}</h3>
        {description && <p>{description}</p>}
      </div>
    </div>
  );
}

function BrandingTab({ config, t }) {
  const navigate = useNavigate();
  const { currentLanguage } = useSelector((s) => s.language);
  const branding = config?.branding || {};
  const accent = branding.accentColor || '#6366f1';
  const companyName = getLocalized(branding.companyName, currentLanguage) || config?.organization?.name || '';

  return (
    <div className="settings-card">
      <SectionHeader
        icon={FiDroplet}
        title={t('settings.branding.title')}
        description={t('settings.branding.description')}
      />
      <div className="settings-card__body">
        <div className="branding-cta" style={{ '--brand-accent': accent }}>
          <div className="branding-cta__main">
            <div className="branding-cta__icon" aria-hidden="true">
              <FiDroplet />
            </div>
            <div className="branding-cta__text">
              <h3>{t('settings.branding.studioTitle', { defaultValue: 'Branding Studio' })}</h3>
              <p>
                {t('settings.branding.studioDescription', {
                  defaultValue: 'Design your login page — layout, colors, typography, slider, buttons. Preview any change in a full-screen viewer.',
                })}
              </p>
              {companyName && (
                <span className="branding-cta__hint">
                  {t('settings.branding.studioHint', { defaultValue: 'Editing brand for' })} <strong>{companyName}</strong>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="branding-cta__open"
            onClick={() => navigate('/branding-studio')}
          >
            {t('settings.branding.openStudio', { defaultValue: 'Open Studio' })}
            <FiArrowRight />
          </button>
        </div>
      </div>
    </div>
  );
}

function Settings({ variant = 'account' }) {
  const { t } = useTranslation();
  const { user } = useSelector((s) => s.auth);
  const { config } = useSelector((s) => s.settings);
  const { currentLanguage } = useSelector((s) => s.language);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userRole = user?.role;
  const isAdmin = hasPermission(userRole, 'manage_settings', user?.effectivePermissions);
  const isWorkspace = variant === 'workspace';

  // Account settings (per-user) live under /profile; workspace/tenant settings
  // (Organization & Branding) live under /settings. Keeps user-level and
  // tenant-level configuration cleanly separated.
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // Start on the variant's default tab; a valid ?tab= override is applied by the
  // effect below (avoids landing on an invalid tab if the param is garbage).
  const [activeTab, setActiveTab] = useState(isWorkspace ? 'organization' : 'profile');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    dispatch(fetchTenantConfig());
  }, [dispatch]);

  const TAB_GROUPS = useMemo(() => {
    if (isWorkspace) {
      if (!isAdmin) return [];
      return [
        {
          // No group heading — a single group needs no label ("WORKSPACE" was
          // showing untranslated in the Turkish UI); the items stand alone.
          key: 'workspace',
          items: [
            { id: 'organization', label: t('settings.tabs.organization'), icon: FiBriefcase, hint: t('settings.organization.description') },
            { id: 'branding', label: t('settings.tabs.branding'), icon: FiDroplet, hint: t('settings.branding.description') },
            { id: 'defaults', label: t('settings.tabs.defaults'), icon: FiSliders, hint: t('settings.defaults.description') },
          ],
        },
      ];
    }
    return [
      {
        key: 'account',
        label: t('settings.groups.account', { defaultValue: 'Account' }),
        items: [
          { id: 'profile', label: t('settings.tabs.profile'), icon: FiUser, hint: t('settings.profile.description') },
          { id: 'security', label: t('settings.tabs.security'), icon: FiLock, hint: t('settings.security.description') },
          { id: 'language', label: t('settings.tabs.language'), icon: FiGlobe, hint: t('settings.language.description') },
          // Vekalet sits directly under Language, as the spec places it.
          { id: 'delegation', label: t('delegation.title'), icon: FiUserCheck, hint: t('delegation.description') },
        ],
      },
    ];
  }, [isAdmin, isWorkspace, t]);

  // Deep-link support: honour ?tab= from the URL (e.g. the profile menu's
  // "Preferences" entry opens /profile?tab=language). Only switch when the
  // requested tab actually exists for this variant.
  useEffect(() => {
    if (!tabParam) return;
    const validTab = TAB_GROUPS.some((g) => g.items.some((i) => i.id === tabParam));
    if (validTab) setActiveTab(tabParam);
  }, [tabParam, TAB_GROUPS]);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    department: user?.department || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  // Organization form
  const [orgForm, setOrgForm] = useState({});
  const [savingOrg, setSavingOrg] = useState(false);

  // Full IANA timezone / ISO currency lists for the searchable pickers.
  // Currency names come localized (e.g. "Türk Lirası" under tr).
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const currencyOptions = useMemo(() => getCurrencyOptions(currentLanguage), [currentLanguage]);
  const countryOptions = useMemo(() => getCountryOptions(currentLanguage), [currentLanguage]);

  // Tenant defaults (Settings > Defaults) — how the app first presents itself
  const [defaultsForm, setDefaultsForm] = useState({ sidebarState: 'expanded' });
  const [savingDefaults, setSavingDefaults] = useState(false);

  useEffect(() => {
    if (config?.defaults?.sidebarState) {
      setDefaultsForm({ sidebarState: config.defaults.sidebarState });
    }
  }, [config?.defaults?.sidebarState]);

  const saveDefaults = async (e) => {
    e.preventDefault();
    setSavingDefaults(true);
    try {
      await settingsAPI.updateDefaults(defaultsForm);
      toast.success(t('settings.defaults.saveSuccess'));
      dispatch(fetchTenantConfig());
    } catch (err) { toast.error(err.response?.data?.error || t('settings.defaults.saveFailed')); }
    finally { setSavingDefaults(false); }
  };

  // Language settings
  const [savingTenantLang, setSavingTenantLang] = useState(false);
  const [tenantLangForm, setTenantLangForm] = useState(config?.defaultLanguage || 'en');

  useEffect(() => {
    if (config?.defaultLanguage) {
      setTenantLangForm(config.defaultLanguage);
    }
  }, [config?.defaultLanguage]);

  useEffect(() => {
    if (config?.organization) {
      setOrgForm({
        name: config.organization.name || '',
        industry: config.organization.industry || '',
        country: config.organization.country || '',
        timezone: config.organization.timezone || 'UTC',
        defaultCurrency: config.organization.defaultCurrency || 'USD',
        // Falls back to the default currency for tenants provisioned before
        // the reporting currency existed.
        reportingCurrency: config.organization.reportingCurrency
          || config.organization.defaultCurrency || 'USD',
        contractNumberPrefix: config.organization.contractNumberPrefix || 'CTR',
      });
    }
  }, [config]);

  const departments = (config?.departments || []).filter(d => d.isActive);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await authAPI.updateProfile(profileForm);
      toast.success(t('settings.profile.saveSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('settings.profile.saveFailed'));
    } finally { setSavingProfile(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { toast.error(t('settings.security.passwordMismatch')); return; }
    if (passwordForm.newPassword.length < 8) { toast.error(t('settings.security.passwordTooShort')); return; }
    setSavingPassword(true);
    try {
      await authAPI.changePassword({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword });
      toast.success(t('settings.security.changeSuccess'));
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { toast.error(err.response?.data?.error || t('settings.security.changeFailed')); }
    finally { setSavingPassword(false); }
  };

  const saveOrganization = async (e) => {
    e.preventDefault();
    setSavingOrg(true);
    try {
      await settingsAPI.updateOrganization(orgForm);
      toast.success(t('settings.organization.saveSuccess'));
      dispatch(fetchTenantConfig());
    } catch (err) { toast.error(err.response?.data?.error || t('settings.organization.saveFailed')); }
    finally { setSavingOrg(false); }
  };

  const handleSaveTenantLanguage = async () => {
    setSavingTenantLang(true);
    try {
      await settingsAPI.updateLanguage({ defaultLanguage: tenantLangForm });
      dispatch(setTenantLanguage(tenantLangForm));
      toast.success(t('settings.language.tenantSaveSuccess'));
      dispatch(fetchTenantConfig());
    } catch (err) {
      toast.error(err.response?.data?.error || t('settings.language.tenantSaveFailed'));
    } finally {
      setSavingTenantLang(false);
    }
  };

  // Only claim the preference was saved once it actually reached the profile;
  // otherwise it would silently revert on the next refresh.
  const handleUserLanguageChange = async (lang) => {
    const result = await dispatch(setUserLanguage(lang));
    if (setUserLanguage.fulfilled.match(result) && result.payload?.saved) {
      toast.success(t('settings.language.userSaveSuccess'));
    } else {
      toast.error(t('settings.language.userSaveFailed'));
    }
  };

  const initials = `${(user?.firstName || '?').charAt(0)}${(user?.lastName || '').charAt(0)}`.toUpperCase();
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';
  const roleLabel = (user?.role || '').replace(/_/g, ' ');

  const activeMeta = TAB_GROUPS.flatMap(g => g.items).find(i => i.id === activeTab);

  // The account variant is the user's own space (profile, security, language,
  // delegation) — calling it "Settings" reads as tenant configuration, so it
  // carries its own heading. Used for the page title and the breadcrumb root.
  const areaTitle = isWorkspace ? t('nav.settings') : t('settings.accountTitle');

  const LANGUAGES = SUPPORTED_LANGUAGES.map((code) => {
    const meta = getLanguageMeta(code);
    return {
      code,
      label: t(`common.${meta.label?.toLowerCase()}`, { defaultValue: meta.label }),
      native: meta.nativeLabel || meta.label,
      flag: meta.flag || code.toUpperCase(),
    };
  });

  const passwordStrength = (() => {
    const p = passwordForm.newPassword;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  return (
    <div className="settings-page">
      {/* The page title is the sidebar entry; the workspace/user identity is
          supporting context under it, not the heading itself. */}
      <PageHeader
        // Workspace settings are a sub-module of Administration in the sidebar;
        // account settings are reached from the profile menu, so they carry no
        // group label.
        label={isWorkspace ? t('nav.administration', 'Administration') : undefined}
        labelIcon={isWorkspace ? <FiSettings /> : undefined}
        title={areaTitle}
        description={isWorkspace ? t('pageDescriptions.settings') : t('pageDescriptions.profile')}
      >
        <div className="settings-hero">
          <div className="settings-hero__glow" aria-hidden="true" />
          <div className="settings-hero__content">
            <div className="settings-hero__avatar" aria-hidden="true">
              {isWorkspace ? <FiBriefcase /> : <span>{initials}</span>}
            </div>
            <div className="settings-hero__info">
              <span className="settings-hero__name">
                {isWorkspace
                  ? (config?.organization?.name || t('settings.groups.workspace', { defaultValue: 'Workspace' }))
                  : fullName}
              </span>
              <div className="settings-hero__meta">
                {isWorkspace ? (
                  <>
                    {config?.organization?.industry && (
                      <span><FiBriefcase /> {config.organization.industry}</span>
                    )}
                    {roleLabel && (
                      <span className="settings-hero__chip"><FiShield /> {roleLabel}</span>
                    )}
                  </>
                ) : (
                  <>
                    {user?.email && <span><FiAtSign /> {user.email}</span>}
                    {roleLabel && (
                      <span className="settings-hero__chip"><FiShield /> {roleLabel}</span>
                    )}
                    {config?.organization?.name && (
                      <span><FiBriefcase /> {config.organization.name}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageHeader>

      <div className="settings-page__layout">
        {/* Rail navigation */}
        <aside className="settings-rail">
          {TAB_GROUPS.map((group) => (
            <div key={group.key} className="settings-rail__group">
              {group.label && <div className="settings-rail__group-label">{group.label}</div>}
              {group.items.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`settings-rail__item ${active ? 'settings-rail__item--active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <span className="settings-rail__icon"><Icon /></span>
                    <span className="settings-rail__text">
                      <strong>{tab.label}</strong>
                    </span>
                    <FiChevronRight className="settings-rail__chevron" />
                  </button>
                );
              })}
            </div>
          ))}
        </aside>

        {/* Content */}
        <div className="settings-page__content">
          {activeMeta && (
            <div className="settings-page__breadcrumb">
              <span>{areaTitle}</span>
              <FiChevronRight />
              <span className="settings-page__breadcrumb-current">{activeMeta.label}</span>
            </div>
          )}

          {/* Profile */}
          {activeTab === 'profile' && (
            <div className="settings-card">
              <SectionHeader
                icon={FiUser}
                title={t('settings.profile.title')}
                description={t('settings.profile.description')}
              />
              <form onSubmit={saveProfile} className="settings-card__body">
                <div className="settings-page__form-grid">
                  <div className="form-group">
                    <label>{t('users.form.firstName')}</label>
                    <input name="firstName" value={profileForm.firstName} onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>{t('users.form.lastName')}</label>
                    <input name="lastName" value={profileForm.lastName} onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('users.form.email')}</label>
                  <input value={user?.email || ''} disabled />
                  <span className="settings-page__help-text">{t('settings.profile.emailHelp')}</span>
                </div>
                <div className="settings-page__form-grid">
                  <div className="form-group">
                    <label>{t('users.form.phone')}</label>
                    <input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} placeholder="+1 (555) 123-4567" />
                  </div>
                  <div className="form-group">
                    <label>{t('users.form.department')}</label>
                    <select value={profileForm.department} onChange={(e) => setProfileForm({ ...profileForm, department: e.target.value })}>
                      <option value="">{t('users.form.selectDepartment')}</option>
                      {departments.map((d) => <option key={d._id} value={d.name}>{d.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('users.columns.role')}</label>
                  <input value={roleLabel} disabled />
                  <span className="settings-page__help-text">{t('settings.profile.roleHelp')}</span>
                </div>
                <div className="settings-page__form-actions">
                  <button type="submit" className="btn btn-primary" disabled={savingProfile}>
                    <FiSave /> {savingProfile ? t('common.saving') : t('settings.profile.save')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security */}
          {activeTab === 'security' && (
            <div className="settings-card">
              <SectionHeader
                icon={FiLock}
                title={t('settings.security.title')}
                description={t('settings.security.description')}
              />
              <form onSubmit={changePassword} className="settings-card__body">
                <div className="form-group">
                  <label>{t('settings.security.currentPassword')}</label>
                  <div className="settings-page__input-icon">
                    <input
                      name="currentPassword"
                      type={showCurrentPw ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      required
                    />
                    <button type="button" className="settings-page__input-toggle" onClick={() => setShowCurrentPw(v => !v)} aria-label="Toggle password visibility">
                      {showCurrentPw ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                </div>
                <div className="settings-page__form-grid">
                  <div className="form-group">
                    <label>{t('settings.security.newPassword')}</label>
                    <div className="settings-page__input-icon">
                      <input
                        name="newPassword"
                        type={showNewPw ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        required
                        minLength={8}
                      />
                      <button type="button" className="settings-page__input-toggle" onClick={() => setShowNewPw(v => !v)} aria-label="Toggle password visibility">
                        {showNewPw ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {passwordForm.newPassword && (
                      <div className={`settings-page__pw-meter settings-page__pw-meter--${passwordStrength}`}>
                        <span /><span /><span /><span />
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('settings.security.confirmPassword')}</label>
                    <input
                      name="confirmPassword"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <span className="settings-page__help-text">{t('settings.security.passwordHint')}</span>
                <div className="settings-page__form-actions">
                  <button type="submit" className="btn btn-primary" disabled={savingPassword}>
                    <FiLock /> {savingPassword ? t('common.saving') : t('settings.security.changePassword')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Language */}
          {activeTab === 'language' && (
            <div className="settings-card">
              <SectionHeader
                icon={FiGlobe}
                title={t('settings.language.title')}
                description={t('settings.language.description')}
              />
              <div className="settings-card__body">
                <div className="settings-page__section">
                  <div className="settings-page__section-heading">
                    <h4>{t('settings.language.userPreference')}</h4>
                    <p>{t('settings.language.userPreferenceHelp')}</p>
                  </div>
                  <div className="settings-page__lang-options">
                    {LANGUAGES.map((lang) => {
                      const active = currentLanguage === lang.code;
                      return (
                        <button
                          type="button"
                          key={lang.code}
                          className={`settings-page__lang-option ${active ? 'settings-page__lang-option--active' : ''}`}
                          onClick={() => handleUserLanguageChange(lang.code)}
                        >
                          <span className="settings-page__lang-flag">{lang.flag}</span>
                          <span className="settings-page__lang-text">
                            <strong>{lang.label}</strong>
                            <span className="settings-page__lang-native">{lang.native}</span>
                          </span>
                          {active && <FiCheck className="settings-page__lang-check" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {isAdmin && (
                  <div className="settings-page__section settings-page__section--bordered">
                    <div className="settings-page__section-heading">
                      <h4>{t('settings.language.tenantDefault')}</h4>
                      <p>{t('settings.language.tenantDefaultHelp')}</p>
                    </div>
                    <div className="settings-page__lang-options">
                      {LANGUAGES.map((lang) => {
                        const active = tenantLangForm === lang.code;
                        return (
                          <button
                            type="button"
                            key={lang.code}
                            className={`settings-page__lang-option ${active ? 'settings-page__lang-option--active' : ''}`}
                            onClick={() => setTenantLangForm(lang.code)}
                          >
                            <span className="settings-page__lang-flag">{lang.flag}</span>
                            <span className="settings-page__lang-text">
                              <strong>{lang.label}</strong>
                              <span className="settings-page__lang-native">{lang.native}</span>
                            </span>
                            {active && <FiCheck className="settings-page__lang-check" />}
                          </button>
                        );
                      })}
                    </div>
                    <div className="settings-page__form-actions">
                      <button className="btn btn-primary" onClick={handleSaveTenantLanguage} disabled={savingTenantLang}>
                        <FiSave /> {savingTenantLang ? t('common.saving') : t('settings.language.saveTenantDefault')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick access to granting a delegation from Preferences —
                    the place people land when they are about to be away. */}
                <div className="settings-page__section settings-page__section--bordered">
                  <div className="settings-page__section-heading">
                    <h4>{t('delegation.title')}</h4>
                    <p>{t('delegation.description')}</p>
                  </div>
                  <div className="settings-page__form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate('/profile?tab=delegation&new=1')}
                    >
                      <FiPlus /> {t('delegation.new')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate('/profile?tab=delegation')}
                    >
                      {t('delegation.manage')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delegation (Vekalet) */}
          {activeTab === 'delegation' && <DelegationTab />}

          {/* Organization */}
          {activeTab === 'organization' && isAdmin && (
            <div className="settings-card">
              <SectionHeader
                icon={FiBriefcase}
                title={t('settings.organization.title')}
                description={t('settings.organization.description')}
              />
              <form onSubmit={saveOrganization} className="settings-card__body">
                <div className="settings-page__form-grid">
                  <div className="form-group">
                    <label>{t('settings.organization.companyName')}</label>
                    <input value={orgForm.name || ''} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>{t('settings.organization.industry')}</label>
                    <select value={orgForm.industry || ''} onChange={(e) => setOrgForm({ ...orgForm, industry: e.target.value })}>
                      <option value="">{t('settings.organization.selectIndustry')}</option>
                      <option value="Technology">{t('settingsExtra.industries.technology')}</option>
                      <option value="Finance">{t('settingsExtra.industries.finance')}</option>
                      <option value="Healthcare">{t('settingsExtra.industries.healthcare')}</option>
                      <option value="Legal">{t('settingsExtra.industries.legal')}</option>
                      <option value="Manufacturing">{t('settingsExtra.industries.manufacturing')}</option>
                      <option value="Real Estate">{t('settingsExtra.industries.realEstate')}</option>
                      <option value="Retail">{t('settingsExtra.industries.retail')}</option>
                      <option value="Education">{t('settingsExtra.industries.education')}</option>
                      <option value="Government">{t('settingsExtra.industries.government')}</option>
                      <option value="Other">{t('settingsExtra.industries.other')}</option>
                    </select>
                  </div>
                </div>
                <div className="settings-page__form-grid">
                  <div className="form-group">
                    <label>{t('settings.organization.country')}</label>
                    <SearchableSelect
                      value={orgForm.country || ''}
                      onChange={(v) => setOrgForm({ ...orgForm, country: v })}
                      options={countryOptions}
                      placeholder={t('settings.organization.selectCountry')}
                      clearable
                    />
                    <span className="settings-page__help-text">{t('settings.organization.countryHelp')}</span>
                  </div>
                  <div className="form-group">
                    <label>{t('settings.organization.currency')}</label>
                    <SearchableSelect
                      value={orgForm.defaultCurrency || 'USD'}
                      onChange={(v) => setOrgForm({ ...orgForm, defaultCurrency: v })}
                      options={currencyOptions}
                    />
                    <span className="settings-page__help-text">
                      {t('settings.organization.currencyHelp')}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>{t('settings.organization.reportingCurrency')}</label>
                    <SearchableSelect
                      value={orgForm.reportingCurrency || orgForm.defaultCurrency || 'USD'}
                      onChange={(v) => setOrgForm({ ...orgForm, reportingCurrency: v })}
                      options={currencyOptions}
                    />
                    <span className="settings-page__help-text">
                      {t('settings.organization.reportingCurrencyHelp')}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>{t('settings.organization.timezone')}</label>
                    <SearchableSelect
                      value={orgForm.timezone || 'UTC'}
                      onChange={(v) => setOrgForm({ ...orgForm, timezone: v })}
                      options={timezoneOptions}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('settings.organization.contractPrefix')}</label>
                  <input value={orgForm.contractNumberPrefix || ''} onChange={(e) => setOrgForm({ ...orgForm, contractNumberPrefix: e.target.value.toUpperCase() })} maxLength={6} style={{ maxWidth: '200px' }} />
                  <span className="settings-page__help-text">{t('settings.organization.contractPrefixHelp', { prefix: orgForm.contractNumberPrefix || 'CTR' })}</span>
                </div>
                <div className="settings-page__form-actions">
                  <button type="submit" className="btn btn-primary" disabled={savingOrg}>
                    <FiSave /> {savingOrg ? t('common.saving') : t('settings.organization.save')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Branding */}
          {activeTab === 'branding' && isAdmin && (
            <BrandingTab config={config} t={t} />
          )}

          {/* Tenant defaults — the app's first presentation after login.
              Future tenant-level defaults (language, currency, list view…)
              belong on this card too. */}
          {activeTab === 'defaults' && isAdmin && (
            <div className="settings-card">
              <SectionHeader
                icon={FiSliders}
                title={t('settings.defaults.title')}
                description={t('settings.defaults.description')}
              />
              <form onSubmit={saveDefaults} className="settings-card__body">
                <div className="settings-page__form-grid">
                  <div className="form-group">
                    <label><FiSidebar style={{ verticalAlign: '-2px', marginRight: 6 }} />{t('settings.defaults.sidebarState')}</label>
                    <select
                      value={defaultsForm.sidebarState}
                      onChange={(e) => setDefaultsForm({ ...defaultsForm, sidebarState: e.target.value })}
                    >
                      <option value="expanded">{t('settings.defaults.sidebarExpanded')}</option>
                      <option value="collapsed">{t('settings.defaults.sidebarCollapsed')}</option>
                    </select>
                    <span className="settings-page__help-text">{t('settings.defaults.sidebarStateHelp')}</span>
                  </div>
                </div>
                <div className="settings-page__form-actions">
                  <button type="submit" className="btn btn-primary" disabled={savingDefaults}>
                    <FiSave /> {savingDefaults ? t('common.saving') : t('settings.defaults.save')}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
